import { create, tsx } from '@dojo/framework/core/vdom';
import { Link } from '@dojo/framework/routing/Link';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { FAQ } from '../../widgets/FAQ';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { Summary } from '../../widgets/Summary';
import SummaryList from '../../widgets/SummaryList';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';

const analysisImg = require('../../assets/images/illustration/analysis.svg');
const appDev = require('../../assets/images/illustration/app-dev.svg');
const cultureImg = require('../../assets/images/icon-illustration/engineering-culture.svg');
const highPerformanceImg = require('../../assets/images/illustration/high-performance.svg');
const legacyImg = require('../../assets/images/icon-illustration/legacy-software-modernization.svg');
const researchImg = require('../../assets/images/icon-illustration/enterprise-ux-research.svg');
const uiDesignImg = require('../../assets/images/icon-illustration/enterprise-app-ui-design.svg');
const uxDesignImg = require('../../assets/images/icon-illustration/enterprise-software-ux-design.svg');

const factory = create();

export const EnterpriseTechnologyStrategy = factory(function EnterpriseTechnologyStrategy() {
	return (
		<div>
			<Head
				title="Enterprise Technology Strategy and Architecture Consulting | SitePen"
				description="Enterprise technology strategy and architecture consulting to help innovate, modernize, and implement systems and solutions to transform performance across the enterprise."
			/>

			<Hero background="level2BlueGreen" paddingBottom="sm">
				{{
					xlTitle: (
						<div>
							Strategy and
							<br />
							Architecture
						</div>
					)
				}}
			</Hero>

			<Section>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={highPerformanceImg} size="lg" />,
							title: 'Accelerate Your Innovation And Transformation',
							blurb: (
								<virtual>
									<p>
										Technology underpins a huge part of today’s global economy,
										with ever-increasing numbers of organizations establishing
										themselves as tech companies, or at a minimum, utilizing
										software technology and online commerce for large portions
										of their business operation. As businesses grow in size, the
										suite of technology assets they depend on scales with them -
										along with spiralling complexity and maintenance costs.
										These downsides result in larger inertia to change, meaning
										businesses become slow to adapt to market forces and can
										expend great cost attempting and failing to capture new
										opportunities.
									</p>
									<p>
										Defining and executing a technology strategy is key to
										addressing these issues, enabling technology to be a helper
										rather than a hindrance in delivery business success. It is
										important to map out which technology capabilities are core
										to a business, how cost-effective they are to maintain, how
										they can best be combined and repurposed to expose
										additional value, as well as where any gaps may lie when
										looking to fulfill the wider business goals. Analyzing a
										business’s existing technology landscape to extract such
										information and planning around how to reach the intended
										state can reveal areas where capabilities are duplicated or
										costly to maintain - areas where investment is better
										diverted to implementing more cost-effective replacements as
										well as filling gaps where capabilities may be missing.
									</p>
									<p>
										With an effective technology strategy in place, businesses
										can ensure they are making best use of their technology
										assets in supporting their current and future activities, as
										well as allowing greater agility in adapting to changing
										markets and the pursuit of new opportunities.
									</p>
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'Our Strategy & Processes',
							items: [
								{
									title: 'Product Vision, Discovery, Analysis, & Architecture',
									blurb: (
										<virtual>
											SitePen provides end-to-end solution delivery expertise
											that maximizes efficiency of your existing technology
											assets, as well as planning and implementing new
											software solutions to augment an existing capability
											suite. Our expertise ranges from strategy definition and
											execution advice,{' '}
											<Link to="servicesEnterpriseArchitecture">
												enterprise architecture
											</Link>{' '}
											and{' '}
											<Link to="servicesSoftwareArchitecture">
												solution architecture
											</Link>
											, product ideation and requirements analysis, as well as{' '}
											<Link to="servicesCustomSoftwareDevelopment">
												solution implementation
											</Link>
											. We can help growing companies looking to establish a
											new technology strategy, and firms looking for
											improvements to their existing strategies - including
											delivery of new strategic solutions and capabilities.
										</virtual>
									),
									image: (
										<Image src={uiDesignImg} lazy size="icon" align="center" />
									)
								},
								{
									title: 'DevOps Excellence',
									blurb: (
										<virtual>
											Successful software systems rely on robust
											infrastructure to operate. Planning for timelines and
											costs associated with infrastructure is essential within
											a larger technology strategy - especially trying to
											minimize this overhead. SitePen offers extensive
											expertise in{' '}
											<Link to="servicesEnterpriseDevOpsSolutions">
												cloud infrastructure usage and devops automation
											</Link>{' '}
											to allow for more agile infrastructure management while
											also minimizing ongoing costs. Our approach is to treat
											infrastructure definition and management as close to
											software code as possible, allowing for operational
											agility that matches software delivery.
										</virtual>
									),
									image: (
										<Image src={researchImg} lazy size="icon" align="center" />
									)
								},
								{
									title: 'Innersource & Open Source',
									blurb: `SitePen's solution delivery and strategy expertise relies on the effective use of open source software - as a company we actively support development on several open source libraries as part of this commitment. Whether it be the use of simple libraries, robust middleware platforms, or sophisticated data stores, we are able to bring the most effective industry-favoured open source solutions to bear within the strategies we deliver. Our solutions also champion the philosophy of innersourcing, where firms can reap certain benefits of open source development and maintenance approaches while developing proprietary in-house capabilities. Open source and innersource methodologies help bring down costs as part of an effective technology strategy implementation.`,
									image: (
										<Image
											src={uxDesignImg}
											lazy
											size="icon"
											align="center"
											constrainedBy="height"
										/>
									)
								},
								{
									title: 'Engineering Culture',
									blurb: (
										<virtual>
											SitePen champions{' '}
											<Link to="expertiseAgileConsulting">
												agile engineering practices
											</Link>{' '}
											for robust ongoing delivery that incorporates the wider
											technology and business strategies, as well as evolving
											stakeholder feedback. This approach is used for all
											aspects of our solution analysis, strategy and delivery
											work, giving voice to all stakeholders through
											continuous engagement and iterative delivery phases. Our
											confidence in this approach and the benefits it helps
											deliver allows us to educate stakeholders that may be
											unfamiliar or hesitant, ensuring everyone is brought
											along and that they feel empowered to contribute to a
											successful solution implementation.
										</virtual>
									),
									image: (
										<Image src={cultureImg} lazy size="icon" align="center" />
									)
								},
								{
									title: 'Legacy Software Modernization',
									blurb: (
										<virtual>
											Part of a successful technology strategy includes{' '}
											<Link to="servicesSoftwareModernization">
												long-term support
											</Link>{' '}
											of the software systems underpinning a business. SitePen
											can help in strategizing how this support is most
											effectively carried out over time - whether it be
											ongoing maintenance of existing capabilities, planning
											for some form of phased uplift, or a complete
											replacement in line with new strategic directives. Our
											expertise allows us to analyze and inform on how best to
											achieve the wider business and technology objectives
											with an existing suite of software capabilities,
											including migration of intellectual property (data,
											users, processes) from legacy to strategic platforms.
										</virtual>
									),
									image: <Image src={legacyImg} lazy size="icon" align="center" />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<SummaryList
						style="alternating"
						trailingBias="medium"
						listGuttering="none"
						itemPaddingBottom="md"
					>
						{{
							items: [
								{
									title: 'Technology Strategy vs Business Strategy',
									blurb: (
										<virtual>
											Technology and business strategies are tightly coupled -
											a technology strategy exists to support a business
											strategy, and increasingly so in today's world, a
											business strategy cannot be executed without the help of
											a technological underpinning. For companies in the
											technology industry, there may be no differentiation
											between the two types of strategy. In all cases, a
											technology strategy exists to support and implement the
											overall business strategy through the most effective use
											of software processes and capabilities.
										</virtual>
									),
									image: (
										<Image src={appDev} size="xl" constrainedBy="height" lazy />
									)
								},
								{
									title: 'Aligning Technology With Strategy',
									blurb: `SitePen's strengths across technology architecture, analysis, strategy definition, and solution delivery gives us insight into how best to implement a successful technology strategy for your business. Our expertise covers a wide range of industries, technologies, and business scales - we are able to help whether you are looking at implementing a new technology strategy, assessing alignment of an existing strategy and asset suite to your business objectives, or help with implementation of new capabilities that meet your overall strategic direction.`,
									image: (
										<Image
											src={analysisImg}
											size="xl"
											constrainedBy="height"
											lazy
										/>
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ConnectStrip />
			</Section>
			<Section trailing>
				<FAQ title="Enterprise Technology Consulting FAQs">
					{[
						{
							title: 'What Is Technology Strategy?',
							content: (
								<p>
									A technology strategy is a plan for how technology resources
									(particularly software systems and the capabilities they expose)
									can be implemented and exploited to best achieve the wider
									business objectives. Technology strategies detail the set of
									high-level objectives that the technology division strives for
									in support of the business, details on the most effective means
									and methods of how the objectives can be met, as well as framing
									of all assumptions made as part of the planning process. A
									successful technology strategy should be understandable by all
									stakeholders across technology and business leadership, and have
									buy-in from all parties. It also needs to be disseminated across
									a business's technology division so that all employees are aware
									of the wider goals and how best to achieve them within their day
									to day work.
								</p>
							)
						},
						{
							title: 'How Significant Is Technology To The Strategy Of A Business?',
							content: (
								<p>
									Usage of software technology is a major factor in the
									profitability of many businesses today, particularly for
									businesses dealing with information as their primary product.
									Increasing numbers of businesses can be classed as technology
									companies in terms of where significant portions of their costs
									are allocated, such as payroll for technology staff and
									infrastructure costs to run the software that delivers customer
									value. It is critical that firms of all sizes realize the
									importance that efficient use of software and related
									technologies has to the ongoing health of their business - and
									in not doing so, the significant wastes of investment, time and
									resources that can result.
								</p>
							)
						},
						{
							title: 'How To Create A Technology Strategy?',
							content: (
								<p>
									Technology divisions need to work closely with their business
									partners to understand what the overall business strategy is,
									including the rationale behind it. With this framing in place,
									technology leaders can formulate a plan around how to assemble a
									suite of technology assets and capabilities in the most
									efficient manner possible that aligns with and supports success
									of the business strategy. Several key pieces of information are
									required in this process. A full inventory of existing
									technology systems and the capabilities they offer will provide
									a set of puzzle pieces that can be combined into solving the
									needs of the business. Gaps are likely to be encountered, where
									existing capabilities are incomplete or missing entirely - for
									these, objectives need to be stated on how the gaps can be most
									effectively filled. This may include options to evaluate and
									purchase third-party solutions; or, for capabilities that will
									support unique selling propositions of the business, plans on
									how the missing capabilities can be created and maintained
									in-house. All these factors need to be balanced with available
									or desired technology resources, meaning the most effective
									organizational structure of the technology division that will
									implement and maintain the technology asset suite. Cost
									management is another critical factor in finding a balanced
									strategic path forward.
								</p>
							)
						},
						{
							title: 'How Are Strategy And Technology Related?',
							content: (
								<p>
									Software technology is the major driving force powering many
									businesses in today's internet-driven world, and will only
									increase in prevalence over time. As this unfolds, business
									strategies are becoming technology strategies - technology is
									critical to achieving business success. Without technology, such
									businesses could not function. Conversely, overuse or
									mismanagement of technology assets can be detrimental, eating
									away at the bottom line through unnecessary costs. Formulating a
									strategy that seeks an optimal middle ground of effective and
									efficient use of technology is key to achieving continued
									business success.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default EnterpriseTechnologyStrategy;
